import { JBAction } from './actions'

/**
 * Actions that structurally mutate the workflow graph.
 * All of these are blocked when the journey is in "paused" status.
 */
const STRUCTURAL_ACTIONS = new Set([
  JBAction.NODE_DROPPED_FROM_PALETTE,
  JBAction.NODES_MOVED,
  JBAction.NODE_REMOVED,
  JBAction.NODE_COPIED,
  JBAction.NODE_LINKS_REMOVED,
  JBAction.NODE_REPLACE,
  JBAction.NODE_NEXT_CONNECTION_CREATED,
  JBAction.LINK_CREATED,
  JBAction.LINK_REMOVED,
  JBAction.LINK_TARGET_REPLACED,
  JBAction.LINK_LABEL_CHANGED,
  JBAction.COPY_SELECTION,
  JBAction.CUT_SELECTION,
  JBAction.PASTE,
  JBAction.REMOVE_SELECTION,
  JBAction.AUTO_LAYOUT_WORKFLOW,
  JBAction.UNDO,
  JBAction.REDO
])

/**
 * Fields inside NODE_FORMDATA_CHANGED that represent edges (connections between nodes).
 * Changing these fields would structurally alter the graph, so they are stripped from
 * the payload when the journey is paused.
 *
 * Affected node types: if-else → else_target; case → default_target, cases[*].target
 */
const IF_ELSE_EDGE_FIELDS = new Set(['else_target', 'target'])
const CASE_EDGE_FIELDS = new Set(['default_target', 'target'])

/**
 * Strip edge-related fields from NODE_FORMDATA_CHANGED payloads so that
 * structural graph changes cannot slip through via form edits while paused.
 *
 * @param {string} nodeType
 * @param {Object} data - The form data object from the action payload.
 * @returns {Object} A sanitised copy of data with edge fields removed.
 */
function sanitiseFormData(nodeType, data) {
  if (!data || typeof data !== 'object') return data

  const sanitised = { ...data }

  if (nodeType === 'if-else') {
    IF_ELSE_EDGE_FIELDS.forEach(field => delete sanitised[field])
  }

  if (nodeType === 'case') {
    CASE_EDGE_FIELDS.forEach(field => delete sanitised[field])
    // Strip per-case target fields inside the cases array
    if (Array.isArray(sanitised.cases)) {
      sanitised.cases = sanitised.cases.map(c => {
        const { target: _target, edge_uid: _edge_uid, ...rest } = c
        return rest
      })
    }
  }

  return sanitised
}

/**
 * Middleware that enforces read-only behaviour for structural workflow actions
 * when the journey is in "paused" status.
 *
 * Allowed while paused:
 *   - NODE_DATA_CHANGED      — config/property edits via the properties panel
 *   - NODE_FORMDATA_CHANGED  — form edits (edge-related fields are stripped for if-else/case)
 *   - All UI / zoom / selection / validation actions
 *
 * Blocked while paused (silently dropped):
 *   - Any action in STRUCTURAL_ACTIONS (adding nodes, removing nodes, connecting
 *     nodes, moving nodes, clipboard operations, undo/redo, auto-layout, etc.)
 *
 * @type {function(MiddlewareAPI): function(Dispatch): function(Action): any}
 */
export const pausedJourneyMiddleware =
  ({ getState }) =>
    next =>
      action => {
        const state = getState()

        if (state?.mode !== 'paused') {
          return next(action)
        }

        // Block structural actions outright
        if (STRUCTURAL_ACTIONS.has(action.type)) {
          return
        }

        // Allow NODE_FORMDATA_CHANGED but strip edge-related fields so graph
        // structure cannot change through form submissions.
        if (action.type === JBAction.NODE_FORMDATA_CHANGED) {
          const { nodeId, data } = action.payload
          const nodeType = state.workflow?.nodes?.[nodeId]?.type
          if (nodeType) {
            const sanitised = sanitiseFormData(nodeType, data)
            return next({ ...action, payload: { nodeId, data: sanitised } })
          }
        }

        return next(action)
      }
